import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  ArrowLeft,
  MapPin,
  Sparkles,
  Loader2,
  Train,
  Bus,
  TramFront,
  ShoppingCart,
  GraduationCap,
  School,
  Bed,
  Bath,
  Sofa,
  Zap,
  ChevronLeft,
  ChevronRight,
  Camera,
  
} from 'lucide-react';
import L from 'leaflet';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import {
  cacheListingAmenityCoords,
  cacheListingAmenities,
  cacheListingStreetView,
  hasListingDraftValue,
  readListingAmenityCoords,
  readListingAmenities,
  readListingDraft,
  readListingStreetViewState,
  updateListingDraft,
  updateListingStreetViewState,
} from '../lib/listingCache';

const TRANSPORT_ICON = { tube: TramFront, tram: TramFront, train: Train, bus: Bus };
const SCHOOL_ICON    = { nursery: School, primary: School, school: GraduationCap };
const WALKING_METERS_PER_MINUTE = 80;

function metersToWalkingMinutes(distanceMeters) {
  if (distanceMeters == null) return null;
  return Math.max(1, Math.round(Number(distanceMeters) / WALKING_METERS_PER_MINUTE));
}

function walkingMinutesToMeters(minutes) {
  return Math.max(0, Math.round(Number(minutes) * WALKING_METERS_PER_MINUTE));
}

function transportKind(types = []) {
  if (types.some(t => ['subway_station', 'light_rail_station', 'metro_station'].includes(t))) return 'tube';
  if (types.some(t => t === 'train_station')) return 'train';
  if (types.some(t => ['bus_station', 'bus_stop'].includes(t))) return 'bus';
  return 'train';
}
function schoolKind(types = [], name = '') {
  if (types.includes('preschool') || /nursery|infant/i.test(name)) return 'nursery';
  if (types.includes('primary_school') || /primary|junior/i.test(name)) return 'primary';
  return 'school';
}

// Deduplicate places by normalised name WITHIN the same kind, keeping closest
function deduplicatePlaces(places) {
  const normalise = (n) => n
    .toLowerCase()
    .replace(/\s*\([^)]*\)/g, '')   // remove "(Stop A)", "(Stop X)" etc.
    .replace(/\bstation\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
  const seen = new Map();
  for (const place of places) {
    // Include kind in the key so a bus stop and tube station with the same
    // name are NOT merged — they're different things at different locations
    const key = `${place.kind}::${normalise(place.name)}`;
    if (!seen.has(key) || (place.distance ?? Infinity) < (seen.get(key).distance ?? Infinity)) {
      seen.set(key, place);
    }
  }
  return [...seen.values()].sort((a, b) => (a.distance ?? 0) - (b.distance ?? 0));
}

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const SESSION_TOKEN_KEY = 'siyak_access_token';

function readSessionToken() {
  try {
    return sessionStorage.getItem(SESSION_TOKEN_KEY);
  } catch {
    return null;
  }
}

function withAuthFetchOptions(options = {}) {
  const token = readSessionToken();
  const headers = new Headers(options.headers || {});

  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  return {
    ...options,
    credentials: 'include',
    headers,
  };
}

const FEATURE_OPTIONS = [
  { key: 'parking', label: 'Parking' },
  { key: 'garden', label: 'Garden' },
  { key: 'balcony', label: 'Balcony' },
  { key: 'pets_allowed', label: 'Pets Allowed' },
  { key: 'bills_included', label: 'Bills Included' },
  { key: 'double_glazing', label: 'Double Glazing' },
  { key: 'central_heating', label: 'Central Heating' },
  { key: 'fitted_kitchen', label: 'Fitted Kitchen' },
  { key: 'near_transport', label: 'Near Transport' },
  { key: 'near_schools', label: 'Near Schools' },
  { key: 'new_build', label: 'New Build' },
  { key: 'wheelchair_accessible', label: 'Wheelchair Access' },
];

const EPC_BANDS = [
  { letter: 'A', colour: 'bg-green-500', text: 'text-green-700', light: 'bg-green-50 border-green-300' },
  { letter: 'B', colour: 'bg-green-400', text: 'text-green-600', light: 'bg-green-50 border-green-200' },
  { letter: 'C', colour: 'bg-lime-400',  text: 'text-lime-700',  light: 'bg-lime-50 border-lime-300' },
  { letter: 'D', colour: 'bg-yellow-400',text: 'text-yellow-700',light: 'bg-yellow-50 border-yellow-300' },
  { letter: 'E', colour: 'bg-orange-400',text: 'text-orange-700',light: 'bg-orange-50 border-orange-300' },
  { letter: 'F', colour: 'bg-orange-600',text: 'text-orange-800',light: 'bg-orange-50 border-orange-400' },
  { letter: 'G', colour: 'bg-red-500',   text: 'text-red-700',   light: 'bg-red-50 border-red-300' },
];

function epcBand(letter) {
  return EPC_BANDS.find((b) => b.letter === letter);
}

function listingStreetViewUrl(property) {
  if (!property?.id || !property?.listing_street_view_image) return null;
  return `${API_URL}/uploads/listing-images/${property.id}/${property.listing_street_view_image}`;
}

function staticMapRemoteUrl(lat, lng, zoom = 16, size = '1200x400') {
  return `https://staticmap.openstreetmap.de/staticmap.php?center=${lat},${lng}&zoom=${zoom}&size=${size}&maptype=mapnik&markers=${lat},${lng},red-pushpin`;
}


// Colour palette used for map markers and the legend — keep in sync
const COLOURS = {
  transport:    '#1E90FF', // DodgerBlue
  supermarkets: '#0F766E', // Teal 700
  schools:      '#FFB300', // Amber
  leisure:      '#FF3B30', // Vibrant Red
  parks:        '#65A30D', // Lime 600
};

const PROPERTY_COLOUR = '#4F46E5';

function getListingStatusMeta(property) {
  if (property?.is_occupied) {
    return { label: 'No listing', tone: 'bg-slate-100 text-slate-700 border-slate-200' };
  }
  if (property?.marketing_status === 'listed') {
    return { label: 'Published', tone: 'bg-green-100 text-green-700 border-green-200' };
  }
  if (property?.listing_started) {
    return { label: 'Draft', tone: 'bg-amber-100 text-amber-700 border-amber-200' };
  }
  return { label: 'No listing', tone: 'bg-slate-100 text-slate-700 border-slate-200' };
}

// Module-level SVG marker helper (used by AmenityOverlay outside the component)
function _makeSvgDataUrl(fillColor, size = 14, strokeColor = '#fff', strokeWidth = 2, shape = 'circle') {
  if (shape === 'pin') {
    const svg = `<?xml version="1.0" encoding="UTF-8"?><svg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}' viewBox='0 0 24 24'>` +
      `<path fill='${fillColor}' d='M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z'/><circle cx='12' cy='9' r='2.5' fill='${strokeColor}'/></svg>`;
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  }
  const svg = `<?xml version="1.0" encoding="UTF-8"?><svg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}' viewBox='0 0 ${size} ${size}'>` +
    `<circle cx='${size / 2}' cy='${size / 2}' r='${Math.max(0, (size - strokeWidth) / 2)}' fill='${fillColor}' stroke='${strokeColor}' stroke-width='${strokeWidth}'/></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

// Amenity overlay for Leaflet — defined at module level so React never remounts it on parent re-renders.
// Renders DOM pin markers positioned via latLngToLayerPoint, attached to markerPane.
function AmenityOverlay({ entries = [] }) {
  const map = useMap();
  // Stringify entries as a stable dep to detect content changes
  const entriesKey = JSON.stringify(entries);

  useEffect(() => {
    if (!map || !map.getPanes) return;
    const panes = map.getPanes();
    // markerPane is a div — required for absolute-positioned DOM children.
    // overlayPane is SVG and cannot host divs.
    const hostPane = panes.markerPane || map.getContainer();
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '0';
    container.style.top = '0';
    container.style.pointerEvents = 'none';
    hostPane.appendChild(container);

    const nodes = new Map();

    const update = () => {
      if (!map || !container) return;
      const toKeep = new Set();
      for (const place of entries) {
        if (place.lat == null || place.lng == null) continue;
        const key = `${place.cat}::${place.name}::${place.lat}::${place.lng}`;
        toKeep.add(key);
        let node = nodes.get(key);
        if (!node) {
          node = document.createElement('div');
          node.style.position = 'absolute';
          node.style.pointerEvents = 'auto';
          node.style.transform = 'translate(-50%, -100%)';
          node.style.zIndex = '400';
          const colour = COLOURS[place.cat] || '#888';
          const size = 36;
          const img = document.createElement('img');
          img.src = _makeSvgDataUrl(colour, size, '#fff', 1.5, 'pin');
          img.width = size; img.height = size; img.style.display = 'block'; img.style.userSelect = 'none'; img.style.background = 'transparent';
          node.appendChild(img);
          node.title = place.name || '';
          node.onclick = () => { try { map.flyTo([Number(place.lat), Number(place.lng)], Math.max(map.getZoom(), 16)); } catch (e) {} };
          container.appendChild(node);
          nodes.set(key, node);
        }
        try {
          const p = map.latLngToLayerPoint([Number(place.lat), Number(place.lng)]);
          node.style.left = `${Math.round(p.x)}px`;
          node.style.top = `${Math.round(p.y)}px`;
        } catch (e) { /* ignore */ }
      }
      for (const [k, n] of nodes.entries()) {
        if (!toKeep.has(k)) {
          try { if (n.parentNode) n.parentNode.removeChild(n); } catch (e) {}
          nodes.delete(k);
        }
      }
    };

    update();
    map.on('move zoom zoomend resize viewreset', update);

    return () => {
      try { map.off('move zoom zoomend resize viewreset', update); } catch (e) {}
      try { if (container.parentNode) container.parentNode.removeChild(container); } catch (e) {}
      nodes.clear();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, entriesKey]);

  return null;
}

// Manual property pin + radius circle — both drawn imperatively so we own
// their appearance without any Leaflet component styling getting in the way.
function PropertyOverlay({ lat, lng, radius }) {
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    // Radius circle — use L.circle so Leaflet manages the SVG projection correctly
    const circle = L.circle([lat, lng], {
      radius,
      color: '#6366F1',
      fillColor: '#6366F1',
      fillOpacity: 0.07,
      opacity: 0.5,
      weight: 1.5,
      interactive: false,
    }).addTo(map);

    // Property pin — manual div in markerPane so we fully control appearance
    const markerPane = map.getPanes().markerPane;
    const pin = document.createElement('div');
    pin.style.position = 'absolute';
    pin.style.pointerEvents = 'none';
    pin.style.transform = 'translate(-50%, -100%)';
    pin.style.zIndex = '500';
    const pinSize = 40;
    const pinImg = document.createElement('img');
    pinImg.src = _makeSvgDataUrl(PROPERTY_COLOUR, pinSize, '#fff', 2, 'pin');
    pinImg.width = pinSize; pinImg.height = pinSize;
    pinImg.style.display = 'block'; pinImg.style.background = 'transparent';
    pin.appendChild(pinImg);
    markerPane.appendChild(pin);

    const updatePin = () => {
      const p = map.latLngToLayerPoint([lat, lng]);
      pin.style.left = `${Math.round(p.x)}px`;
      pin.style.top = `${Math.round(p.y)}px`;
    };

    updatePin();
    map.on('move zoom zoomend resize viewreset', updatePin);

    return () => {
      try { map.off('move zoom zoomend resize viewreset', updatePin); } catch (e) {}
      try { circle.remove(); } catch (e) {}
      try { if (pin.parentNode) pin.parentNode.removeChild(pin); } catch (e) {}
    };
  }, [map, lat, lng, radius]);

  return null;
}

async function fetchAmenities(location, radiusDefault = 800, { propertyId = null, postcode = null } = {}) {
  // `location` can be either a postcode string, or an object { lat, lng }.
  const isCoords = location && typeof location === 'object' && location.lat != null && location.lng != null;
  const locLabel = isCoords ? `${location.lat},${location.lng}` : String(location);
  console.group(`[Amenities] fetchAmenities("${locLabel}")`);
  console.debug('[Amenities] Using OpenStreetMap Overpass API');

  try {
    let lat, lng;
    if (isCoords) {
      ({ lat, lng } = location);
      console.debug(`[Amenities] Using provided coords → ${lat}, ${lng}`);
    } else {
      console.debug('[Amenities] Geocoding via postcodes.io...');
      const geo = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(location)}`);
      if (!geo.ok) throw new Error(`postcodes.io ${geo.status}`);
      const { result } = await geo.json();
      lat = result?.latitude;
      lng = result?.longitude;
      if (lat == null || lng == null) throw new Error('geocode-failed');
      console.debug(`[Amenities] Geocoded → ${lat}, ${lng}`);
    }

    const cachedDraft = propertyId ? readListingDraft(propertyId) : null;
    const _ac = cachedDraft?.amenitiesCache;
    const _cacHasData = _ac && (
      (_ac.transport?.length > 0) || (_ac.supermarkets?.length > 0) ||
      (_ac.schools?.length > 0)  || (_ac.leisure?.length > 0)       || (_ac.parks?.length > 0)
    );
    if (_cacHasData && Date.now() - (_ac._ts || 0) < 24 * 60 * 60 * 1000) {
      console.debug('[Amenities] returning cached per-property draft results');
      console.groupEnd();
      return {
        transport: _ac.transport || [],
        supermarkets: _ac.supermarkets || [],
        schools: _ac.schools || [],
        leisure: _ac.leisure || [],
        parks: _ac.parks || [],
        coords: cachedDraft.amenityCoords || { lat, lng },
      };
    }

    // Cache miss — prefer the backend endpoint (avoids client-side Overpass rate limits).
    if (!isCoords && API_URL) {
      try {
        console.debug('[Amenities] trying backend nearby-amenities endpoint');
        const be = await fetch(
          `${API_URL}/api/landlord/nearby-amenities?postcode=${encodeURIComponent(location)}`,
          withAuthFetchOptions()
        );
        if (be.ok) {
          const body = await be.json();
          console.debug('[Amenities] backend raw response', body);
          const mapPlace = (p) => ({ name: p.name, distance: p.distance, lat: p.lat ?? null, lng: p.lng ?? null, kind: p.kind ?? null });
          const transport    = (body.transport    || []).map(mapPlace);
          const supermarkets = (body.supermarkets || []).map(mapPlace);
          const schools      = (body.schools      || []).map(mapPlace);
          const leisure      = (body.leisure      || []).map(mapPlace);
          const parks        = (body.parks        || []).map(mapPlace);
          const hasData = (transport.length + supermarkets.length + schools.length + leisure.length + parks.length) > 0;
          if (hasData) {
            console.debug('[Amenities] backend returned data', { transport, supermarkets, schools, leisure, parks });
            if (propertyId) {
              cacheListingAmenities(propertyId, {
                amenities: { transport, supermarkets, schools, leisure, parks },
                coords: { lat, lng },
              });
            }
            console.groupEnd();
            return { transport, supermarkets, schools, leisure, parks, coords: { lat, lng } };
          }
          console.debug('[Amenities] backend returned no results; falling back to client Overpass');
        }
      } catch (e) {
        console.debug('[Amenities] backend nearby-amenities failed, falling back to client Overpass', e);
      }
    }

    const runOverpass = async (query) => {
      const url = 'https://overpass-api.de/api/interpreter';
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `data=${encodeURIComponent(query)}`,
      });
      if (!res.ok) throw new Error(`overpass ${res.status}`);
      return res.json();
    };

    const buildAround = (filters, radius) => {
      // Expand each filter to search nodes, ways and relations so areas
      // (e.g. parks, school polygons) are included and we get centre coords.
      return filters.map((f) => {
        const base = f.replace(/^(node|way|relation)/, '');
        return `node${base}(around:${radius},${lat},${lng});way${base}(around:${radius},${lat},${lng});relation${base}(around:${radius},${lat},${lng});`;
      }).join('');
    };

    const mapElementsToPlaces = (elements) => {
      const R = 6371000;
      return elements.map((el) => {
        const elLat = el.lat ?? el.center?.lat ?? null;
        const elLng = el.lon ?? el.center?.lon ?? null;
        let distance = null;
        if (elLat != null && elLng != null) {
          const dLat = ((elLat - lat) * Math.PI) / 180;
          const dLng = ((elLng - lng) * Math.PI) / 180;
          const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat * Math.PI) / 180) * Math.cos((elLat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
          distance = Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
        }
        const name = el.tags?.name || el.tags?.ref || '';
        return { el, name, distance, lat: elLat, lng: elLng };
      });
    };

    const queryCategory = async (key, filters, radius) => {
      try {
        const q = `[out:json][timeout:20];(${buildAround(filters, radius)});out center;`;
        const data = await runOverpass(q);
        const elems = (data.elements || []).filter(e => (e.tags && (e.tags.name || e.tags.ref)));
        const mapped = mapElementsToPlaces(elems).map(p => {
          const kind = key === 'transport' ? transportKind(Object.keys(p.el.tags || {}))
                     : key === 'schools' ? schoolKind(Object.keys(p.el.tags || []), p.name)
                     : null;
          return { name: p.name, distance: p.distance, kind, lat: p.lat, lng: p.lng };
        });
        const limit = key === 'supermarkets' ? 5 : 8;
        return deduplicatePlaces(mapped).slice(0, limit);
      } catch (err) {
        console.warn(`[Amenities] overpass ${key} error`, err);
        return [];
      }
    };

    const [transport, supermarkets, schools, leisure, parks] = await Promise.all([
      queryCategory('transport', [
        'node["public_transport"="platform"]',
        'node["public_transport"="stop_position"]',
        'node["public_transport"="station"]',
        'node["highway"="bus_stop"]',
        'node["railway"="station"]',
        'node["railway"="tram_stop"]',
      ], radiusDefault),
      queryCategory('supermarkets', [
        'node["shop"="supermarket"]',
        'node["shop"="grocery"]',
        'node["shop"="convenience"]',
      ], radiusDefault),
      queryCategory('schools', [
        'node["amenity"="school"]',
        'node["amenity"="kindergarten"]',
        'node["amenity"="nursery"]',
      ], Math.max(1000, Math.round(radiusDefault * 1.25))),
      queryCategory('leisure', [
        'node["leisure"="fitness_centre"]',
        'node["leisure"="sports_centre"]',
        'node["leisure"="stadium"]',
        'node["leisure"="swimming_pool"]',
        'node["amenity"="gym"]',
      ], Math.max(1000, Math.round(radiusDefault * 1.5))),
      queryCategory('parks', [
        'node["leisure"="park"]',
        'node["leisure"="garden"]',
      ], Math.max(800, Math.round(radiusDefault * 0.9))),
    ]);

    console.debug('[Amenities] Done →', { transport, supermarkets, schools, leisure, parks });
    const clientHasData = (transport.length + supermarkets.length + schools.length + leisure.length + parks.length) > 0;
    if (propertyId && clientHasData) {
      cacheListingAmenities(propertyId, {
        amenities: { transport, supermarkets, schools, leisure, parks },
        coords: { lat, lng },
      });
    }
    console.groupEnd();
    return { transport, supermarkets, schools, leisure, parks, coords: { lat, lng } };
  } catch (err) {
    console.error('[Amenities] fetchAmenities threw:', err);
    console.groupEnd();
    return { transport: [], supermarkets: [], schools: [], leisure: [], parks: [], coords: null };
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function ListingBuilder() {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const initialDraft = useRef(readListingDraft(propertyId)).current;
  const initialStreetViewState = useRef(readListingStreetViewState(propertyId)).current;
  const initialAmenities = useRef(readListingAmenities(propertyId, null, 2000)).current;

  const [property,         setProperty]         = useState(null);
  const [listingImages,    setListingImages]    = useState([])
  const [uploadingImage,   setUploadingImage]   = useState(false)
  const [imageError,       setImageError]       = useState(null)
  const [heroIndex,        setHeroIndex]        = useState(0)
  const [slideOrder,       setSlideOrder]       = useState(() => initialDraft.slideOrder || null) // null = not yet initialised
  const [dragIndex,        setDragIndex]        = useState(null)
  const touchDrag = useRef({ from: null, overIndex: null }) // touch drag state
  const [description,      setDescription]      = useState(() => initialDraft.description || '');
  const [features,         setFeatures]         = useState(() => initialDraft.features || []);
  const [highlights,       setHighlights]       = useState(() => initialDraft.highlights || '');
  const [epcRating,        setEpcRating]        = useState(() => initialDraft.epcRating || '');
  const [generating,       setGenerating]       = useState(false);
  const [isPublishing,     setIsPublishing]     = useState(false);
  const [source,           setSource]           = useState(null);
  const [generationsRemaining, setGenerationsRemaining] = useState(null);
  const [amenities,        setAmenities]        = useState(() => ({
    transport: initialAmenities.transport || [],
    supermarkets: initialAmenities.supermarkets || [],
    schools: initialAmenities.schools || [],
    leisure: initialAmenities.leisure || [],
    parks: initialAmenities.parks || [],
    custom: initialAmenities.custom || initialDraft.customAmenities || [],
  }));
  const [amenityRadius,    setAmenityRadius]    = useState(10); // walking minutes
  const [customAmenityDraft, setCustomAmenityDraft] = useState('');

  const [amenityCoords,    setAmenityCoords]    = useState(() => readListingAmenityCoords(propertyId));
  const [amenitiesLoading, setAmenitiesLoading] = useState(false);
  const [amenitiesView,    setAmenitiesView]    = useState('list');
  // show interactive map by default for snappy UX
  const [showInteractiveMap, setShowInteractiveMap] = useState(true);

  const [staticMapError, setStaticMapError] = useState(false);
  const [svError,          setSvError]          = useState(() => initialStreetViewState.error);
  const [svHidden,         setSvHidden]         = useState(() => initialStreetViewState.hidden);
  const [svDataUrl,        setSvDataUrl]        = useState(() => initialStreetViewState.dataUrl);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishedUrl, setPublishedUrl] = useState('');
  const amenityRadiusMeters = walkingMinutesToMeters(amenityRadius);

  useEffect(() => {
    axios.get(`${API_URL}/api/properties/${propertyId}`, { withCredentials: true })
      .then((res) => {
        const p = res.data;
        setProperty(p);
        // For description and highlights: prefer any locally-cached value (the user may have
        // edited the field before the debounced backend save could fire). Only load from the
        // backend when no local cache entry exists — matching the EPC immediate-save behaviour.
        if (p.description && !hasListingDraftValue(propertyId, 'description')) {
          setDescription(p.description);
          updateListingDraft(propertyId, { description: p.description });
        }
        if (p.epc_rating && !hasListingDraftValue(propertyId, 'epcRating')) {
          setEpcRating(p.epc_rating);
          updateListingDraft(propertyId, { epcRating: p.epc_rating });
        }
        if (p.highlights?.length && !hasListingDraftValue(propertyId, 'highlights')) {
          const hl = p.highlights.join(', ');
          setHighlights(hl);
          updateListingDraft(propertyId, { highlights: hl });
        }
        if (p.features?.length && !hasListingDraftValue(propertyId, 'features')) {
          setFeatures(p.features);
          updateListingDraft(propertyId, { features: p.features });
        }
        if (p.listing_images?.length) {
          setListingImages(p.listing_images)
          setHeroIndex(0)
        }
      })
      .catch(() => navigate('/landlord'));
  }, [propertyId, navigate]);

  // Cache description, highlights and EPC rating in localStorage so they survive page refreshes
  useEffect(() => {
    updateListingDraft(propertyId, { description });
  }, [propertyId, description]);
  useEffect(() => {
    updateListingDraft(propertyId, { highlights });
  }, [propertyId, highlights]);
  useEffect(() => {
    updateListingDraft(propertyId, { features });
  }, [propertyId, features]);
  useEffect(() => {
    updateListingDraft(propertyId, { customAmenities: amenities.custom || [] });
  }, [propertyId, amenities.custom]);
  useEffect(() => {
    updateListingDraft(propertyId, { epcRating });
  }, [propertyId, epcRating]);

  // Save timers removed — description and highlights now save immediately on every keystroke.
  const descSaveTimer = useRef(null);
  const hlSaveTimer   = useRef(null);

  // If the property already has coordinates, use them for the amenities map immediately
  useEffect(() => {
    if (!property) return;
    if (property.lat != null && property.lng != null && !amenityCoords) {
      setAmenityCoords({ lat: property.lat, lng: property.lng });
      cacheListingAmenityCoords(propertyId, property.postcode, { lat: property.lat, lng: property.lng });
    }
  }, [property, amenityCoords, propertyId]);

  // If we still don't have coords, try a lightweight geocode of the postcode
  useEffect(() => {
    if (!property || amenityCoords) return;
    if (!property.postcode) return;
    const cachedCoords = readListingAmenityCoords(propertyId, property);
    if (cachedCoords?.lat != null && cachedCoords?.lng != null) {
      setAmenityCoords(cachedCoords);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(property.postcode)}`);
        if (!res.ok) return;
        const d = await res.json();
        const lat = d?.result?.latitude;
        const lng = d?.result?.longitude;
        if (!cancelled && lat != null && lng != null) {
          cacheListingAmenityCoords(propertyId, property.postcode, { lat, lng });
          setAmenityCoords({ lat, lng });
        }
      } catch (e) { /* ignore */ }
    })();

    return () => { cancelled = true; };
  }, [property, amenityCoords, propertyId]);

  // Fetch Street View via backend proxy (avoids CORS) and cache as data URL in localStorage.
  useEffect(() => {
    if (!property?.id || svError || svHidden) return;
    const storedStreetViewUrl = listingStreetViewUrl(property);
    if (storedStreetViewUrl) {
      cacheListingStreetView(property.id, storedStreetViewUrl);
      setSvDataUrl(storedStreetViewUrl);
      return;
    }

    const cachedState = readListingStreetViewState(property.id);
    if (cachedState.dataUrl) { setSvDataUrl(cachedState.dataUrl); return; }

    const address = property.address || '';
    const postcode = property.postcode || '';
    if (!address && !postcode) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/landlord/street-view-proxy?property_id=${encodeURIComponent(property.id)}&address=${encodeURIComponent(address)}&postcode=${encodeURIComponent(postcode)}`,
          withAuthFetchOptions()
        );
        if (!res.ok) throw new Error(`sv-proxy ${res.status}`);
        const contentType = res.headers.get('content-type') || 'image/jpeg';
        const extension = contentType.includes('png') ? 'png' : 'jpg';
        const dataUrl = `${API_URL}/uploads/listing-images/${property.id}/street_view.${extension}`;
        if (!cancelled) {
          cacheListingStreetView(property.id, dataUrl);
          setSvDataUrl(dataUrl);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('[ListingBuilder] Street View proxy failed', err);
          setSvError(true);
          updateListingStreetViewState(property.id, { streetViewError: true });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [property, svError, svHidden]);

  // Keep slideOrder in sync with available slides (map + uploaded images).
  // Preserves any custom drag order; only appends new items / removes deleted ones.
  useEffect(() => {
    const hasMap = !svError && !svHidden && !!svDataUrl;
    setSlideOrder((prev) => {
      const all = [...(hasMap ? ['sv'] : []), ...listingImages];
      if (prev === null) return all;                              // first init
      const valid = prev.filter((id) => all.includes(id));
      const added = all.filter((id) => !valid.includes(id));
      return [...valid, ...added];
    });
  }, [svError, svHidden, svDataUrl, listingImages, property]);

  useEffect(() => {
    if (slideOrder === null) return;
    updateListingDraft(propertyId, { slideOrder });
  }, [propertyId, slideOrder]);

  // Auto-fetch amenities once when the property first loads.
  // Fetch at full 2000m — the radius slider only controls which results are displayed.
  useEffect(() => {
    if (!property) return;
    const locationArg = property.postcode || (property.lat != null && property.lng != null
      ? { lat: property.lat, lng: property.lng }
      : null);
    if (!locationArg) return;

    let cancelled = false;
    setAmenitiesLoading(true);
    if (property.lat != null && property.lng != null) {
      setAmenityCoords({ lat: property.lat, lng: property.lng });
      cacheListingAmenityCoords(propertyId, property.postcode, { lat: property.lat, lng: property.lng });
    }
    const cachedAmenityData = readListingAmenities(propertyId, property, 2000);
    const hasCachedAmenityData = (
      (cachedAmenityData.transport?.length || 0) +
      (cachedAmenityData.supermarkets?.length || 0) +
      (cachedAmenityData.schools?.length || 0) +
      (cachedAmenityData.leisure?.length || 0) +
      (cachedAmenityData.parks?.length || 0)
    ) > 0;
    if (hasCachedAmenityData) {
      setAmenities((prev) => ({ ...cachedAmenityData, custom: prev.custom || cachedAmenityData.custom || [] }));
      if (cachedAmenityData.coords) setAmenityCoords((prev) => prev?.lat ? prev : cachedAmenityData.coords);
    }
    fetchAmenities(locationArg, 2000, { propertyId, postcode: property.postcode })
      .then(({ coords, ...rest }) => {
        if (cancelled) return;
        setAmenities((prev) => ({ ...rest, custom: prev.custom || [] }));
        if (coords) setAmenityCoords((prev) => prev?.lat ? prev : coords);
      })
      .finally(() => { if (!cancelled) setAmenitiesLoading(false); });
    return () => { cancelled = true; };
  }, [property, propertyId]);

  const manualRefetchAmenities = useCallback(() => {
    if (!property || amenitiesLoading) return;
    const postcode = property.postcode;
    if (!postcode || !API_URL) return;
    setAmenitiesLoading(true);
    // Call the backend with bust=true to evict its 1-hour in-memory cache and
    // trigger a full fresh mirror-raced Overpass fetch — same path as the auto-fetch.
    fetch(
      `${API_URL}/api/landlord/nearby-amenities?postcode=${encodeURIComponent(postcode)}&radius=2000&bust=true`,
      withAuthFetchOptions()
    )
      .then(async (res) => {
        if (!res.ok) throw new Error(`nearby-amenities ${res.status}`);
        const body = await res.json();
        const mapPlace = (p) => ({ name: p.name, distance: p.distance, lat: p.lat ?? null, lng: p.lng ?? null, kind: p.kind ?? null });
        const fresh = {
          transport:    (body.transport    || []).map(mapPlace),
          supermarkets: (body.supermarkets || []).map(mapPlace),
          schools:      (body.schools      || []).map(mapPlace),
          leisure:      (body.leisure      || []).map(mapPlace),
          parks:        (body.parks        || []).map(mapPlace),
        };
        setAmenities((prev) => ({ ...fresh, custom: prev.custom || [] }));
        const coords = amenityCoords || (property.lat != null ? { lat: property.lat, lng: property.lng } : null);
        cacheListingAmenities(propertyId, { amenities: fresh, coords });
      })
      .catch((e) => console.warn('[Amenities] manual refetch failed', e))
      .finally(() => setAmenitiesLoading(false));
  }, [property, propertyId, amenitiesLoading, amenityCoords]);

  const toggleFeature = (key) => {
    setFeatures((prev) => {
      const next = prev.includes(key) ? prev.filter((f) => f !== key) : [...prev, key];
      updateListingDraft(propertyId, { features: next });
      saveDraftSilently({ features: next });
      return next;
    });
  };

  const addCustomAmenity = useCallback(() => {
    const name = customAmenityDraft.trim();
    if (!name) return;
    setAmenities((prev) => {
      const existing = prev.custom || [];
      if (existing.some((item) => item.name?.toLowerCase() === name.toLowerCase())) return prev;
      return {
        ...prev,
        custom: [...existing, { name, distance: null, kind: 'custom', lat: null, lng: null }],
      };
    });
    setCustomAmenityDraft('');
  }, [customAmenityDraft]);

  const removeCustomAmenity = useCallback((name) => {
    setAmenities((prev) => ({
      ...prev,
      custom: (prev.custom || []).filter((item) => item.name !== name),
    }));
  }, []);

  const handleGenerate = async () => {
    if (!property) return;
    setGenerating(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/landlord/generate-listing-description`,
        {
          property_type: property.property_type || property.type || 'property',
          bedrooms:   property.bedrooms  ?? 0,
          bathrooms:  property.bathrooms ?? 0,
          rent:       property.rent      ?? 0,
          postcode:   property.postcode  || '',
          address:    property.address   || '',
          furnishing: property.furnishing ?? null,
          features,
          highlights: highlights.split(',').map((s) => s.trim()).filter(Boolean),
          epc_rating: epcRating || null,
          amenities: {
            transport:    amenities.transport    || [],
            supermarkets: amenities.supermarkets || [],
            schools:      amenities.schools      || [],
            leisure:      amenities.leisure      || [],
            parks:        amenities.parks        || [],
            custom:       amenities.custom       || [],
          },
          property_id: propertyId,
        },
        { withCredentials: true }
      );
      setDescription(res.data.description);
      setSource(res.data.source);
      if (res.data.generations_remaining !== undefined) {
        setGenerationsRemaining(res.data.generations_remaining);
      }
    } catch {
      toast.error('Could not generate description. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      setImageError('Only JPEG and PNG images are supported.')
      return
    }
    if (file.size > 10 * 1024 * 1024) {
      setImageError('Image must be under 10MB.')
      return
    }
    setImageError(null)
    setUploadingImage(true)
    const formData = new FormData()
    formData.append('file', file)
    try {
      const res = await axios.post(
        `${API_URL}/api/landlord/properties/${propertyId}/listing-images`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      )
      setListingImages((prev) => [...prev, res.data.filename])
      setProperty((prev) => prev ? ({ ...prev, listing_started: true }) : prev)
      setHeroIndex(9999) // clamped to last slide after re-render
    } catch (err) {
      const detail = err.response?.data?.detail
      setImageError(detail || 'Upload failed. Please try another image.')
    } finally {
      setUploadingImage(false)
      e.target.value = ''
    }
  }

  const handleDeleteImage = async (filename) => {
    try {
      await axios.delete(
        `${API_URL}/api/landlord/properties/${propertyId}/listing-images/${filename}`,
        { withCredentials: true }
      )
      setSlideOrder((prev) => prev ? prev.filter((id) => id !== filename) : prev)
      setListingImages((prev) => prev.filter((f) => f !== filename))
      setHeroIndex((idx) => Math.max(0, idx - 1))
    } catch {
      toast.error('Could not remove image.')
    }
  }

  const draftPayload = (overrides = {}) => ({
    description: overrides.description !== undefined ? overrides.description : description,
    highlights: (overrides.highlights ?? highlights).split(',').map((s) => s.trim()).filter(Boolean),
    epc_rating: (overrides.epc_rating !== undefined ? overrides.epc_rating : epcRating) || null,
    features: overrides.features !== undefined ? overrides.features : features,
  });

  const saveDraftSilently = async (overrides = {}) => {
    try {
      await axios.put(`${API_URL}/api/properties/${propertyId}`, { ...draftPayload(overrides), listing_started: true }, { withCredentials: true });
      setProperty((prev) => prev ? ({ ...prev, listing_started: true }) : prev);
    } catch { /* silent */ }
  };

  const isPublished = property?.marketing_status === 'listed';
  const listingStatus = getListingStatusMeta(property);

  const handlePublish = async () => {
    if (property?.is_occupied) {
      toast.error('Mark the property unoccupied before publishing the listing.');
      return;
    }
    setIsPublishing(true);
    try {
      const newStatus = isPublished ? 'not_listed' : 'listed';
      const response = await axios.put(
        `${API_URL}/api/properties/${propertyId}`,
        { ...draftPayload(), marketing_status: newStatus, listing_started: true },
        { withCredentials: true }
      );
      const updatedProperty = response.data || {};
      setProperty((p) => ({ ...p, ...updatedProperty }));
      const actualStatus = updatedProperty.marketing_status || newStatus;
      toast.success(actualStatus === 'listed' ? 'Listing published!' : 'Listing unpublished.');
      if (actualStatus === 'listed') {
        const listingUrl = `${window.location.origin}/properties/${property?.id || propertyId}`;
        setPublishedUrl(listingUrl);
        setPublishDialogOpen(true);
      }
    } catch {
      toast.error('Failed to update listing status.');
    } finally {
      setIsPublishing(false);
    }
  };

  const handleCopyPublishedUrl = async () => {
    if (!publishedUrl) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(publishedUrl);
      } else {
        const tempInput = document.createElement('input');
        tempInput.value = publishedUrl;
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand('copy');
        document.body.removeChild(tempInput);
      }
      toast.success('Listing URL copied');
    } catch {
      toast.error('Could not copy URL');
    }
  };

  const handleSharePublishedUrl = async () => {
    if (!publishedUrl) return;
    try {
      if (navigator.share) {
        await navigator.share({
          title: property?.address || 'Property listing',
          text: 'Property listing on SIYAK',
          url: publishedUrl,
        });
        return;
      }
      await handleCopyPublishedUrl();
    } catch {
      toast.error('Could not share listing URL');
    }
  };

  if (!property) {
    return (
      <div className="min-h-screen bg-navy-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
      </div>
    );
  }

  const svUrl = (!svError && !svHidden && svDataUrl) ? svDataUrl : null;
  // Build ordered slides from the drag-managed slideOrder state
  const orderedSlides = (slideOrder || []).map((id) => {
    if (id === 'sv') return svUrl ? { type: 'sv', src: svUrl, id: 'sv' } : null;
    return { type: 'img', src: `${API_URL}/uploads/listing-images/${propertyId}/${id}`, filename: id, id };
  }).filter(Boolean);
  const boundedHeroIndex = Math.max(0, Math.min(heroIndex, orderedSlides.length - 1));
  const heroSrc = orderedSlides[boundedHeroIndex]?.src ?? null;
  const propertyType = property.property_type || property.type || 'Property';
  const band = epcRating ? epcBand(epcRating) : null;

  // -------------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-navy-50">
      {/* ── Thin top bar ── */}
      <div className="bg-white border-b border-border px-4 py-3">
        <div className="max-w-6xl mx-auto flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/landlord')}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-navy-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
          <span className="text-navy-200">|</span>
          <span className="text-sm font-medium text-navy-900">Listing Builder</span>
          <Badge variant="outline" className={listingStatus.tone}>{listingStatus.label}</Badge>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">

        {/* ══════════════════════════════════════════════
            HERO IMAGE
        ══════════════════════════════════════════════ */}
        <div className="relative w-full h-72 md:h-96 rounded-2xl overflow-hidden bg-navy-100 mb-0 shadow-sm">
          {heroSrc ? (
            <>
              <img
                src={heroSrc}
                alt="Listing hero"
                className="w-full h-full object-cover"
                onError={() => {
                  const current = orderedSlides[boundedHeroIndex];
                  if (!current) return;
                  if (current.type === 'sv') {
                    setSvError(true);
                    updateListingStreetViewState(propertyId, { streetViewError: true });
                  }
                  else setImageError('Could not load image');
                }}
              />

              {/* Carousel controls when more than one slide exists */}
              {orderedSlides.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() => setHeroIndex((i) => (i - 1 + orderedSlides.length) % orderedSlides.length)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full w-9 h-9 flex items-center justify-center"
                    aria-label="Previous image"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setHeroIndex((i) => (i + 1) % orderedSlides.length)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full w-9 h-9 flex items-center justify-center"
                    aria-label="Next image"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </>
              )}
            </>
          ) : (
            <div className="w-full h-full bg-navy-100 flex flex-col items-center justify-center gap-2">
              <MapPin className="w-10 h-10 text-navy-400" />
              <span className="text-navy-400 text-sm">Street view unavailable</span>
            </div>
          )}
          {/* Gradient overlay (non-interactive so buttons remain clickable) */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />
          {/* Price overlay */}
          <div className="absolute bottom-4 left-5 right-5 flex items-end justify-between">
            <div>
              <p className="text-white text-3xl font-bold leading-none drop-shadow">
                £{Number(property.rent ?? 0).toLocaleString('en-GB')}
                <span className="text-lg font-normal ml-1 opacity-90">pcm</span>
              </p>
              <p className="text-white/80 text-sm mt-1 flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" />
                {(() => {
                  const addr = (property.address || '').trim();
                  const pc   = (property.postcode || '').trim();
                  // Avoid showing postcode twice if it's already embedded in the address
                  if (!addr) return pc;
                  if (pc && addr.toLowerCase().includes(pc.toLowerCase())) return addr;
                  return pc ? `${addr}, ${pc}` : addr;
                })()}
              </p>
            </div>
            <span className="bg-white/20 backdrop-blur-sm text-white text-sm px-3 py-1.5 rounded-full border border-white/30">
              {propertyType}
            </span>
          </div>
        </div>

        {/* ══════════════════════════════════════════════
            KEY FACTS BAR
        ══════════════════════════════════════════════ */}
        <div className="bg-white border border-border rounded-b-xl px-5 py-3.5 mb-6 shadow-sm flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-navy-700">
          <span className="flex items-center gap-1.5">
            <Bed className="w-4 h-4 text-navy-400" />
            <strong>{property.bedrooms ?? 0}</strong>&nbsp;Bedrooms
          </span>
          <span className="hidden sm:block text-navy-200">|</span>
          <span className="flex items-center gap-1.5">
            <Bath className="w-4 h-4 text-navy-400" />
            <strong>{property.bathrooms ?? 0}</strong>&nbsp;Bathrooms
          </span>
          {property.furnishing && (
            <>
              <span className="hidden sm:block text-navy-200">|</span>
              <span className="flex items-center gap-1.5">
                <Sofa className="w-4 h-4 text-navy-400" />
                <span className="capitalize">{property.furnishing}</span>
              </span>
            </>
          )}
          {epcRating && (
            <>
              <span className="hidden sm:block text-navy-200">|</span>
              <span className="flex items-center gap-1.5">
                <Zap className="w-4 h-4 text-navy-400" />
                <span>EPC</span>
                <span className={`inline-flex items-center justify-center w-5 h-5 rounded text-xs font-bold text-white ${band?.colour ?? 'bg-navy-400'}`}>
                  {epcRating}
                </span>
              </span>
            </>
          )}
        </div>

        {/* ══════════════════════════════════════════════
            TWO-COLUMN BODY
        ══════════════════════════════════════════════ */}
        <div className="lg:grid lg:grid-cols-3 lg:gap-6">

          {/* ── LEFT: Listing preview content ── */}
          <div className="lg:col-span-2 space-y-5">

            {/* Feature badges (from selected toggles) */}
            {features.length > 0 && (
              <div className="bg-white rounded-xl border border-border p-5 shadow-sm">
                <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-3">
                  Property Features
                </h2>
                <div className="flex flex-wrap gap-2">
                  {features.map((key) => {
                    const opt = FEATURE_OPTIONS.find((f) => f.key === key);
                    return (
                      <span
                        key={key}
                        className="bg-royal-50 text-royal-700 border border-royal-200 text-sm px-3 py-1 rounded-full"
                      >
                        {opt?.label ?? key}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Description */}
            <div className="bg-white rounded-xl border border-border p-5 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
                  Description
                </h2>
                <span className="text-xs text-muted-foreground">{description.length} chars</span>
              </div>
              <textarea
                className="w-full min-h-[200px] text-[15px] text-navy-700 leading-7 border border-border rounded-lg p-4 focus:outline-none focus:ring-2 focus:ring-royal-500 resize-none placeholder:text-muted-foreground"
                placeholder="Click 'Generate with AI' to draft your listing description, or type your own."
                value={description}
                onChange={(e) => {
                  const v = e.target.value;
                  setDescription(v);
                  updateListingDraft(propertyId, { description: v });
                  clearTimeout(descSaveTimer.current);
                  descSaveTimer.current = setTimeout(() => saveDraftSilently({ description: v }), 500);
                }}
              />
              {source === 'gemini' && (
                <p className="text-xs text-muted-foreground mt-1.5">
                  ✦ Generated by Gemini 1.5 Flash
                </p>
              )}
              {source === 'template' && generationsRemaining === 0 && (
                <p className="text-xs text-amber-500 mt-1.5">
                  ⚠ Daily AI limit reached · description generated from template
                </p>
              )}
              {source === 'template' && generationsRemaining !== 0 && (
                <p className="text-xs text-muted-foreground mt-1.5">
                  ⚠ Generated from template (Gemini unavailable)
                </p>
              )}
            </div>

            {/* Nearby Amenities */}
            <div className="bg-white rounded-xl border border-border p-5 shadow-sm">
              <div className="flex flex-col gap-2 mb-4">
                {/* Title row */}
                <div className="flex items-center gap-2">
                  <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
                    Nearby Amenities
                  </h2>
                  <span className="relative group">
                    <button
                      type="button"
                      onClick={manualRefetchAmenities}
                      disabled={amenitiesLoading}
                      aria-label={amenitiesLoading ? 'Fetching amenities…' : 'Refresh amenities'}
                      className="p-0 border-0 bg-transparent leading-none focus:outline-none"
                    >
                      <Loader2 className={`w-3.5 h-3.5 text-muted-foreground transition-opacity ${amenitiesLoading ? 'animate-spin opacity-100' : 'opacity-30 hover:opacity-70 cursor-pointer'}`} />
                    </button>
                    {/* Tooltip — visible on hover (desktop) and focus-within (mobile tap) */}
                    <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 whitespace-nowrap rounded-md bg-gray-800 px-2 py-1 text-[10px] text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 z-50">
                      {amenitiesLoading ? 'Fetching…' : 'Tap to refresh amenities'}
                      <span className="absolute left-1/2 -translate-x-1/2 top-full border-4 border-transparent border-t-gray-800" />
                    </span>
                  </span>
                </div>
                {/* Controls row — wraps on narrow screens */}
                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-3 sm:gap-y-2">
                  <div className="grid w-full min-w-0 grid-cols-[auto,1fr,auto] items-center gap-2 sm:flex sm:flex-1 sm:items-center">
                    <label className="text-xs text-muted-foreground shrink-0">Radius:</label>
                    <input
                      type="range"
                      min={2}
                      max={20}
                      step={1}
                      value={amenityRadius}
                      onChange={(e) => setAmenityRadius(Number(e.target.value))}
                      className="flex-1 min-w-0"
                    />
                    <span className="text-xs text-right text-navy-600 whitespace-nowrap sm:w-20 sm:shrink-0">{amenityRadius} min walk</span>
                  </div>
                  {/* List / Map toggle */}
                  <div className="flex w-full items-center bg-navy-100 rounded-lg p-0.5 gap-0.5 sm:w-auto sm:shrink-0">
                    {['list', 'map'].map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setAmenitiesView(v)}
                        className={`flex-1 text-xs px-2.5 py-1 rounded-md capitalize transition-colors sm:flex-none ${
                          amenitiesView === v
                            ? 'bg-white text-navy-900 shadow-sm font-medium'
                            : 'text-navy-500 hover:text-navy-700'
                        }`}
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="text"
                    value={customAmenityDraft}
                    onChange={(e) => setCustomAmenityDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addCustomAmenity();
                      }
                    }}
                    placeholder="Add Extras"
                    className="h-8 px-2.5 text-sm border border-border rounded-md flex-1 min-w-[180px]"
                  />
                  <button
                    type="button"
                    onClick={addCustomAmenity}
                    className="h-8 px-3 text-xs rounded-md border border-border bg-white text-navy-700"
                  >
                    Add
                  </button>
                  <span className="text-xs text-muted-foreground">Extras appear in the list only.</span>
                </div>
              </div>
              {/* Map view: show cached static OSM image first, lazy-load interactive Leaflet map */}
              {amenitiesView === 'map' && (
                <div className="mb-4">
                  <div className="w-full h-64 rounded-lg overflow-hidden border border-border relative">
                    {amenityCoords ? (
                      !showInteractiveMap ? (
                        staticMapError ? (
                          <div className="w-full h-64 bg-navy-50 flex items-center justify-center flex-col gap-2">
                            <MapPin className="w-10 h-10 text-navy-400" />
                            <div className="text-sm text-navy-500">Interactive map available</div>
                            <button
                              type="button"
                              onClick={() => setShowInteractiveMap(true)}
                              className="text-xs px-2 py-1 rounded bg-white border border-border text-navy-700 shadow-sm"
                            >
                              Load interactive map
                            </button>
                          </div>
                        ) : (
                          <>
                            <img
                              src={staticMapRemoteUrl(amenityCoords.lat, amenityCoords.lng, 15, '900x300')}
                              alt="Map"
                              className="w-full h-64 object-cover"
                              onError={(e) => { e.currentTarget.onerror = null; setStaticMapError(true); }}
                            />
                            <div className="absolute top-2 right-2">
                              <button
                                type="button"
                                onClick={() => setShowInteractiveMap(true)}
                                className="text-xs px-2 py-1 rounded bg-white border border-border text-navy-700 shadow-sm"
                              >
                                Load interactive map
                              </button>
                            </div>
                          </>
                        )
                      ) : (
                        <MapContainer
                          center={[Number(amenityCoords.lat), Number(amenityCoords.lng)]}
                          zoom={15}
                          scrollWheelZoom={false}
                          className="w-full h-64"
                          zoomControl={true}
                          attributionControl={true}
                        >
                          <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          />
                          <PropertyOverlay lat={Number(amenityCoords.lat)} lng={Number(amenityCoords.lng)} radius={amenityRadiusMeters} />
                          {/* Manual DOM overlay for amenity markers (faster, consistent styling) */}
                          <AmenityOverlay entries={
                            ['transport','supermarkets','schools','leisure','parks']
                              .flatMap((cat) => (amenities[cat] || []).map((p) => ({ ...p, cat })))
                              .filter((p) => p.distance == null || p.distance <= amenityRadiusMeters)
                          } />
                        </MapContainer>
                      )
                    ) : (
                      <div className="w-full h-64 flex items-center justify-center text-sm text-muted-foreground">Map unavailable</div>
                    )}
                  </div>
                  {/* Legend */}
                  <div className="flex flex-wrap gap-3 mt-2">
                    {[
                      { label: 'Transport',    color: COLOURS.transport },
                      { label: 'Supermarkets', color: COLOURS.supermarkets },
                      { label: 'Schools',      color: COLOURS.schools },
                      { label: 'Leisure',      color: COLOURS.leisure },
                      { label: 'Parks',        color: COLOURS.parks },
                      { label: 'Property',     color: PROPERTY_COLOUR },
                    ].map(({ label, color }) => (
                      <span key={label} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* List view */}
              {amenitiesView === 'list' && (
                <div className="divide-y divide-border">
                  {[
                    { key: 'transport',    label: 'Transport',    Icon: Train },
                    { key: 'supermarkets', label: 'Supermarkets', Icon: ShoppingCart },
                    { key: 'schools',      label: 'Schools',      Icon: GraduationCap },
                    { key: 'leisure',      label: 'Leisure',      Icon: Sparkles },
                    { key: 'parks',        label: 'Parks',        Icon: MapPin },
                    { key: 'custom',       label: 'Extras',       Icon: Sparkles },
                  ].map(({ key, label, Icon }) => (
                    <div key={key} className="py-3 first:pt-0 last:pb-0">
                      <p className="text-xs font-semibold text-navy-600 mb-2 flex items-center gap-1.5">
                        <Icon className="w-3.5 h-3.5" />
                        {label}
                      </p>
                      {(() => {
                        const visible = (amenities[key] || [])
                          .filter((p) => p.distance == null || p.distance <= amenityRadiusMeters);
                        if (visible.length === 0) return (<p className="text-sm text-muted-foreground italic">None found nearby</p>);
                        return (
                          <ul className="space-y-1.5">
                            {visible.map((place, i) => {
                              const PlaceIcon = key === 'transport' ? (TRANSPORT_ICON[place.kind] ?? Train)
                                              : key === 'schools'   ? (SCHOOL_ICON[place.kind] ?? GraduationCap)
                                              : key === 'supermarkets' ? ShoppingCart
                                              : key === 'leisure'   ? Sparkles
                                              : key === 'parks'     ? MapPin
                                              : null;
                              return (
                                <li key={i} className="flex items-center justify-between gap-2 min-w-0">
                                  <span className="text-sm text-navy-700 flex items-center gap-1.5 min-w-0">
                                    {PlaceIcon && <PlaceIcon className="w-3.5 h-3.5 text-navy-400 shrink-0" />}
                                    <span className="truncate">{place.name}</span>
                                  </span>
                                  <span className="text-xs text-muted-foreground shrink-0 flex items-center gap-2">
                                    {place.distance != null && `${metersToWalkingMinutes(place.distance)} min walk`}
                                    {key === 'custom' && (
                                      <button
                                        type="button"
                                        onClick={() => removeCustomAmenity(place.name)}
                                        className="text-navy-400 hover:text-navy-700"
                                        aria-label={`Remove ${place.name}`}
                                      >
                                        ×
                                      </button>
                                    )}
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        );
                      })()}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── RIGHT: Sticky action panel ── */}
          <div className="mt-5 lg:mt-0">
            <div className="bg-white rounded-xl border border-border shadow-sm p-5 lg:sticky lg:top-6 space-y-5">
              {/* Photos section */}
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-2">
                  Photos
                </p>

                {/* Thumbnail grid — drag/touch to reorder */}
                {orderedSlides.length > 0 && (
                  <div className="grid grid-cols-3 gap-1.5 mb-2">
                    {orderedSlides.map((slide, i) => (
                      <div
                        key={slide.id}
                        draggable
                        onDragStart={(e) => { setDragIndex(i); e.dataTransfer.setData('text/plain', String(i)); }}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault();
                          const from = Number(e.dataTransfer.getData('text/plain'));
                          if (from === i) return;
                          setSlideOrder((prev) => { const next = [...prev]; const [item] = next.splice(from, 1); next.splice(i, 0, item); return next; });
                          setHeroIndex(i);
                          setDragIndex(null);
                        }}
                        onDragEnd={() => setDragIndex(null)}
                        onTouchStart={() => {
                          touchDrag.current.from = i;
                          touchDrag.current.overIndex = i;
                          setDragIndex(i);
                        }}
                        onTouchMove={(e) => {
                          const touch = e.touches[0];
                          const el = document.elementFromPoint(touch.clientX, touch.clientY);
                          const cell = el?.closest('[data-slide-index]');
                          if (cell) {
                            const idx = Number(cell.dataset.slideIndex);
                            if (idx !== touchDrag.current.overIndex) {
                              touchDrag.current.overIndex = idx;
                              setDragIndex(touchDrag.current.from); // keep source faded
                            }
                          }
                        }}
                        onTouchEnd={() => {
                          const from = touchDrag.current.from;
                          const to = touchDrag.current.overIndex;
                          touchDrag.current.from = null;
                          touchDrag.current.overIndex = null;
                          setDragIndex(null);
                          if (from !== null && to !== null && from !== to) {
                            setSlideOrder((prev) => { const next = [...prev]; const [item] = next.splice(from, 1); next.splice(to, 0, item); return next; });
                            setHeroIndex(to);
                          }
                        }}
                        data-slide-index={i}
                        role="button"
                        tabIndex={0}
                        onClick={() => { setHeroIndex(i); setImageError(null); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { setHeroIndex(i); setImageError(null); } }}
                        className={`relative group aspect-square rounded-lg overflow-hidden bg-slate-100 cursor-grab active:cursor-grabbing transition-opacity ${dragIndex === i ? 'opacity-40' : ''}`}
                      >
                        <img
                          src={slide.src}
                          alt={`Slide ${i + 1}`}
                          className="w-full h-full object-cover"
                        />
                        {i === 0 && (
                          <span className="absolute top-0.5 left-0.5 text-[7px] bg-black/60 text-white px-1 py-px rounded-full leading-tight">
                            Main
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (slide.type === 'sv') {
                              updateListingStreetViewState(propertyId, { streetViewHidden: true });
                              setSvHidden(true);
                            } else {
                              handleDeleteImage(slide.filename);
                            }
                          }}
                          className="absolute top-0.5 right-0.5 bg-black/60 hover:bg-red-600 text-white rounded-full w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-[10px] leading-none"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload button — hidden when at 8 images */}
                {listingImages.length < 8 && (
                  <label className={`flex items-center justify-center gap-2 w-full border-2 border-dashed rounded-lg py-3 cursor-pointer transition-colors text-sm
                    ${uploadingImage
                      ? 'border-slate-200 text-slate-300 pointer-events-none'
                      : 'border-slate-200 text-slate-500 hover:border-royal-400 hover:text-royal-600'
                    }`}>
                    {uploadingImage
                      ? <><Loader2 className="w-4 h-4 animate-spin" /> Checking image...</>
                      : <><Camera className="w-4 h-4" /> Add photo</>
                    }
                    <input
                      type="file"
                      accept="image/jpeg,image/png"
                      className="hidden"
                      onChange={handleImageUpload}
                      disabled={uploadingImage}
                    />
                  </label>
                )}

                {/* Error feedback */}
                {imageError && (
                  <p className="text-xs text-red-500 mt-1.5 leading-snug">{imageError}</p>
                )}

                <p className="text-xs text-muted-foreground mt-1.5">
                  JPEG or PNG · max 10MB · up to 8 photos
                </p>
                <p className="text-xs text-muted-foreground">
                  Images are checked automatically for watermarks and unsuitable content.
                </p>
              </div>

              <div className="border-t border-border" />

              {/* Section: Feature toggles */}
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-2">
                  Highlights
                </p>
                <div className="grid grid-cols-2 gap-1.5">
                  {FEATURE_OPTIONS.map(({ key, label }) => {
                    const active = features.includes(key);
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => toggleFeature(key)}
                        className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors text-left truncate ${
                          active
                            ? 'bg-royal-600 text-white border-royal-600'
                            : 'border-border text-navy-600 hover:border-royal-400 hover:text-royal-600'
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-border" />

              {/* EPC selector */}
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-2">
                  EPC Rating
                </p>
                <div className="flex gap-1.5 flex-wrap">
                  {EPC_BANDS.map(({ letter, colour }) => {
                    const active = epcRating === letter;
                    return (
                      <button
                        key={letter}
                        type="button"
                        onClick={() => {
                          const next = active ? '' : letter;
                          setEpcRating(next);
                          saveDraftSilently({ epc_rating: next });
                        }}
                        className={`w-9 h-9 rounded-lg text-sm font-bold transition-all ${
                          active
                            ? `${colour} text-white shadow-sm scale-105`
                            : 'bg-navy-100 text-navy-500 hover:bg-navy-200'
                        }`}
                      >
                        {letter}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-border" />

              {/* Additional highlights */}
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1.5">
                  Additional Highlights
                </p>
                <textarea
                  rows={3}
                  className="w-full text-sm text-navy-700 border border-border rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-royal-500 placeholder:text-muted-foreground"
                  placeholder="Any other highlights to mention..."
                  value={highlights}
                  onChange={(e) => {
                    const v = e.target.value;
                    setHighlights(v);
                    updateListingDraft(propertyId, { highlights: v });
                    clearTimeout(hlSaveTimer.current);
                    hlSaveTimer.current = setTimeout(() => saveDraftSilently({ highlights: v }), 500);
                  }}
                />
              </div>

              {/* Generate button */}
              <button
                type="button"
                onClick={handleGenerate}
                disabled={generating}
                className="w-full bg-royal-600 hover:bg-royal-700 disabled:opacity-60 text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 transition-colors text-sm"
              >
                {generating
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <Sparkles className="w-4 h-4" />}
                Generate with AI
              </button>
              <p className="text-xs text-muted-foreground text-center -mt-2">
                Powered by Gemini · Equality Act-safe copy
                {generationsRemaining === 0 && (
                  <span className="block mt-0.5 text-amber-500">
                    Daily AI limit reached — template used instead
                  </span>
                )}
                {generationsRemaining !== null && generationsRemaining > 0 && (
                  <span className="block mt-0.5">
                    {generationsRemaining} AI generation{generationsRemaining !== 1 ? 's' : ''} remaining today
                  </span>
                )}
              </p>
              <p className="text-xs text-muted-foreground text-center">
                AI-generated descriptions are drafts only. Always review and edit before publishing. You are responsible for the accuracy of your listing.
              </p>

              {/* Divider */}
              <div className="border-t border-border" />

              {/* Publish */}
              <Button
                className="w-full text-sm font-semibold py-3"
                onClick={handlePublish}
                disabled={isPublishing || property?.is_occupied}
                variant={isPublished ? 'outline' : 'default'}
              >
                {isPublishing && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                {isPublished ? 'Unpublish' : 'Publish Listing'}
              </Button>
              <p className="text-xs text-muted-foreground text-center -mt-2 leading-relaxed">
                {property?.is_occupied
                  ? 'Occupied properties stay as No listing until you mark them unoccupied.'
                  : 'This listing is for your internal records and tenant applications on SIYAK.'}
              </p>

            </div>
          </div>
        </div>
      </div>

      <Dialog open={publishDialogOpen} onOpenChange={setPublishDialogOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Property published</DialogTitle>
            <DialogDescription>
              Your listing is now live. Share this URL or copy it to send directly.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm break-all">
            {publishedUrl}
          </div>

          <DialogFooter className="flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <Button variant="outline" onClick={handleCopyPublishedUrl} className="w-full sm:w-auto">Copy URL</Button>
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
              <Button
                variant="outline"
                className="w-full sm:w-auto"
                onClick={() => {
                  if (!publishedUrl) return;
                  window.open(publishedUrl, '_blank', 'noopener,noreferrer');
                }}
              >
                View live listing
              </Button>
              <Button variant="outline" onClick={handleSharePublishedUrl} className="w-full sm:w-auto">Share</Button>
              <Button onClick={() => navigate('/landlord')} className="w-full sm:w-auto">Back to Dashboard</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
